import React from 'react';
import { useTranslation } from 'react-i18next';
import { Euro, FolderOpen, AlertTriangle, CheckCircle2 } from 'lucide-react';
import AnimatedCounter from '../../AnimatedCounter';
import { DashboardOverview, formatEuro } from './types';

interface KpiCardsProps {
  kpis: DashboardOverview['kpis'];
}

const KpiCards: React.FC<KpiCardsProps> = ({ kpis }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const cards = [
    {
      title: t('dashboardHome.kpis.revenueThisMonth'),
      value: formatEuro(kpis.revenueThisMonth, lang),
      icon: Euro,
      iconClass: 'bg-green-500',
    },
    {
      title: t('dashboardHome.kpis.openProcesses'),
      value: <AnimatedCounter value={kpis.openProcesses} />,
      icon: FolderOpen,
      iconClass: 'bg-blue-500',
    },
    {
      title: t('dashboardHome.kpis.overdueAmount'),
      value: formatEuro(kpis.overdueAmount, lang),
      hint: t('dashboardHome.kpis.overdueCount', { count: kpis.overdueCount }),
      icon: AlertTriangle,
      iconClass: kpis.overdueAmount > 0 ? 'bg-red-500' : 'bg-gray-400',
      valueClass: kpis.overdueAmount > 0 ? 'text-red-600' : undefined,
    },
    {
      title: t('dashboardHome.kpis.quotesConverted'),
      value: <AnimatedCounter value={kpis.quotesConvertedThisMonth} />,
      hint: t('dashboardHome.kpis.conversionRate', { rate: Math.round(kpis.quoteConversionRate * 100) }),
      icon: CheckCircle2,
      iconClass: 'bg-purple-500',
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <div key={card.title} className="card">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">{card.title}</p>
                <p className={`text-xl sm:text-2xl md:text-3xl font-bold tabular-nums ${card.valueClass || 'text-gray-900'}`}>
                  {card.value}
                </p>
                {card.hint && <p className="text-xs sm:text-sm text-gray-500 mt-1">{card.hint}</p>}
              </div>
              <div className={`w-10 h-10 sm:w-12 sm:h-12 ${card.iconClass} rounded-lg flex items-center justify-center flex-shrink-0`}>
                <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default KpiCards;
